import React, { useState } from 'react';
import { GlobeAltIcon, CheckIcon, KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { updateTecDocKey } from '../../api/configApi';

const Settings = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const [tecDocKey, setTecDocKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [keySuccess, setKeySuccess] = useState('');
  const [keyError, setKeyError] = useState('');

  const languages = [
    { 
      code: 'ro', 
      name: 'Română', 
      nativeName: 'Romanian',
      flag: '🇷🇴',
      description: 'Limba română pentru utilizatorii din România'
    },
    { 
      code: 'en', 
      name: 'English', 
      nativeName: 'English',
      flag: '🇬🇧',
      description: 'English language for international users'
    },
    { 
      code: 'hu', 
      name: 'Magyar', 
      nativeName: 'Hungarian',
      flag: '🇭🇺',
      description: 'Magyar nyelv a magyar felhasználók számára'
    }
  ];

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    
    try {
      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Language setting saved:', language);
      setSuccess(t('languageSettingsSaved'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving language setting:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
  };

  const handleSaveTecDocKey = async () => {
    if (!tecDocKey.trim()) {
      setKeyError('API key cannot be empty.');
      return;
    }
    setSavingKey(true);
    setKeySuccess('');
    setKeyError('');
    try {
      await updateTecDocKey(tecDocKey.trim());
      setKeySuccess('TecDoc API key updated successfully.');
      setTecDocKey('');
      setTimeout(() => setKeySuccess(''), 4000);
    } catch (error) {
      console.error('Error updating TecDoc key:', error);
      setKeyError('Failed to update key. Please try again.');
    } finally {
      setSavingKey(false);
    }
  };

  return (
    <div className="bg-gray-50 py-6">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">

        {/* Language Card */}
        <div className="bg-gray-50 shadow-md rounded-2xl border border-slate-200">
          {/* Header */}
          {/* <div className="px-8 py-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                <GlobeAltIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{t('languageSettings')}</h1>
                <p className="mt-1 text-sm text-slate-600">{t('choosePreferredLanguage')}</p>
              </div>
            </div>
          </div> */}

          {/* Content */}
          <div className="p-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('selectInterfaceLanguage')}</h3>
                <p className="text-sm text-slate-600 mb-6">
                  {t('interfaceLanguageDescription')}
                </p>
              </div>

              {/* Language Options */}
              <div className="grid gap-4">
                {languages.map((lang) => (
                  <label key={lang.code} className="cursor-pointer">
                    <div className={`relative flex items-center p-6 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                      language === lang.code
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}>
                      <input
                        type="radio"
                        name="language"
                        value={lang.code}
                        checked={language === lang.code}
                        onChange={(e) => handleLanguageChange(e.target.value)}
                        className="sr-only"
                      />
                      
                      {/* Flag */}
                      <div className="flex-shrink-0 mr-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center text-2xl shadow-sm">
                          {lang.flag}
                        </div>
                      </div>

                      {/* Language Info */}
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <h4 className="text-lg font-semibold text-slate-900">{lang.name}</h4>
                          <span className="ml-2 text-sm text-slate-500">({lang.nativeName})</span>
                        </div>
                        <p className="text-sm text-slate-600">{lang.description}</p>
                      </div>

                      {/* Selected Indicator */}
                      {language === lang.code && (
                        <div className="flex-shrink-0 ml-4">
                          <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center shadow-lg">
                            <CheckIcon className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Current Selection Info */}
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <GlobeAltIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">{t('currentLanguage')}</h4>
                    <p className="text-sm text-slate-600">
                      {languages.find(lang => lang.code === language)?.name} - {languages.find(lang => lang.code === language)?.nativeName}
                    </p>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center">
                    <CheckIcon className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">{success}</span>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="pt-6 border-t border-slate-200">
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white px-8 py-3 rounded-xl text-sm font-semibold transition duration-200 ease-in-out shadow-lg hover:shadow-xl disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('saving')}
                      </>
                    ) : (
                      t('saveLanguageSettings')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TecDoc API Key Section */}
        <div className="mt-6 bg-gray-50 shadow-md rounded-2xl border border-slate-200">
          <div className="p-8">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                <KeyIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">TecDoc API Key</h3>
                <p className="text-sm text-slate-500">Update the global TecDoc integration key stored securely on the server.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={tecDocKey}
                  onChange={(e) => { setTecDocKey(e.target.value); setKeyError(''); }}
                  placeholder="Enter new TecDoc API key"
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                >
                  {showKey ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>

              {keyError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">{keyError}</p>
                </div>
              )}

              {keySuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center">
                  <CheckIcon className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium text-green-800">{keySuccess}</span>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <button
                  onClick={handleSaveTecDocKey}
                  disabled={savingKey}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white px-8 py-3 rounded-xl text-sm font-semibold transition duration-200 ease-in-out shadow-lg hover:shadow-xl disabled:cursor-not-allowed flex items-center"
                >
                  {savingKey ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : 'Update Key'}
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;
